import React, { ReactNode, useEffect, useRef, useState } from 'react';
import QuestionnairePage, { QuestionnairePageOptions } from '@/components/Questionnaires/QuestionnairePage';
import { usePrevious } from '@restart/hooks';
import { Button, ComponentWithProps, InlineLoader, Slider } from '@visalex/core';
import { useFormContext } from 'react-hook-form';

import cx from 'clsx';
import router from 'next/router';
import { useTranslation } from 'next-i18next';
import { parse, stringify } from 'qs';

import styles from './Questionnaire.module.scss';

export type QuestionnaireProps = {
  /**
   * Whether show a loading spin or not
   */

  loading?: boolean;
  /**
   * Whether show dot button controls on the bottom of the form
   */
  showDots?: boolean;

  /**
   * Callback that is triggering on "Next" button click
   * @param pageId is the current page
   * @param answers is a map of questionHandles versus its values
   */
  onPageCompleted?: (pageId: number, answers: { [questionHandle: string]: string }) => void;

  /**
   * Callback that is triggering on "Reset" button click
   */
  onReset?(): void;

  /**
   * A list of pages to be displayed. It may be added dynamically in case of
   * kind of decision trees or it may be static with the beginning on the 1st slide.
   * It depends on `initialPage` prop
   */
  pages: QuestionnairePageOptions[];

  validationMessage?: (pageId: number, errors: Record<string, unknown>) => ReactNode;

  /**
   * Shows whether the slider has all the pages in advance like a regular quiz does
   */
  initialPage?: number;

  /**
   * Change when you need to update the quiz. Useful in cases of detached fields
   * that are governed by refs only.
   */
  forceUpdateCounter?: number;

  /**
   * Flag variable for displaying title on the last page with custom footer
   */
  showTitle?: boolean;
};

type PositionType = {
  x: number;
  scale: number;
  position:
    | 'relative'
    | 'absolute'
    | 'inherit'
    | '-moz-initial'
    | 'initial'
    | 'revert'
    | 'unset'
    | '-webkit-sticky'
    | 'fixed'
    | 'static'
    | 'sticky'
    | undefined;
  opacity: number;
  display: string;
};

const customAnimation = (i: number, activeId: number) => {
  const animation: PositionType = {
    x: (i - activeId) * 30,
    scale: 1,
    position: activeId === i ? 'relative' : 'absolute',
    opacity: activeId === i ? 1 : 0,
    // Keep 1 page from each side of page
    display: activeId === i ? 'flex' : 'none',
  };

  return animation;
};

const Questionnaire: ComponentWithProps<QuestionnaireProps> = ({
  showDots = true,
  initialPage,
  validationMessage,
  className,
  onPageCompleted,
  pages,
  loading,
  onReset,
  showTitle = false,
}) => {
  const { t } = useTranslation();
  const questionnaireRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const {
    formState: { errors },
    watch,
    unregister,
    trigger,
    clearErrors,
  } = useFormContext();
  const values = watch();

  useEffect(() => {
    trigger(
      Object.keys(values).filter((name) =>
        Array.isArray(values[name]) ? values[name].length > 0 : Boolean(values[name])
      )
    );
  }, []);

  let areRequiredFieldsFilled = true;

  pages[currentPage]?.fields.forEach((field) => {
    const isSet = Array.isArray(values[field.name])
      ? values[field.name].length > 0
      : values[field.name] === 0 || Boolean(values[field.name]);

    areRequiredFieldsFilled = areRequiredFieldsFilled && isSet;
  });

  const prevPages = usePrevious<QuestionnairePageOptions[]>(pages);
  const isPageInvalid = Object.keys(errors).some((fieldName) =>
    pages[currentPage].fields.find((f) => f.name === fieldName)
  );

  const prevPageHandler = () => {
    // Pop all the answers for the current page if user decided to go back
    const queryParams = parse(location.search);

    if (pages[currentPage - 1])
      pages[currentPage - 1].fields.forEach((field) => {
        delete queryParams[field.name];
      });

    if (process.env.NODE_ENV !== 'test') {
      router.push(`${location.pathname}?${stringify(queryParams, { arrayFormat: 'brackets' })}`);
    }

    // Update the current page and scroll to top. Validity is always true as
    // user navigates to the page he/she already answered
    const newCurrentPage = Math.max(0, currentPage - 1);

    setCurrentPage(newCurrentPage);
  };

  const nextPageHandler = async () => {
    const isValid = await trigger(
      pages[currentPage].fields
        .filter((el) => el.required || typeof values[el.name] !== 'undefined')
        .map((el) => el.name),
      { shouldFocus: true }
    );

    if (!isValid) return;

    const queryParams = Object.entries(values).reduce((params, [handle, value]) => {
      if (value && value.value) value = value.value;

      if (typeof value !== 'undefined' && value !== '' && value !== null) params[`${handle}`] = value;

      return params;
    }, {} as { [param: string]: string });

    if (pages[currentPage + 1]) clearErrors(pages[currentPage + 1].fields.map((el) => el.name));

    setCurrentPage(currentPage + 1);

    if (process.env.NODE_ENV !== 'test') {
      Object.keys(queryParams).forEach((queryKey) => {
        if (Array.isArray(queryParams[queryKey])) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          queryParams[queryKey] = queryParams[queryKey].map((el) => el.value);
        }
      });
      router.push(`${location.pathname}?${stringify(queryParams, { arrayFormat: 'brackets' })}`, undefined, {
        scroll: false,
      });
    }

    if (onPageCompleted) {
      onPageCompleted(currentPage, queryParams);
    }
  };

  const resetHandler = () => {
    unregister(Object.keys(values));
    setCurrentPage(0);

    if (onReset) {
      onReset();
    }

    router.push(location.pathname);
  };

  useEffect(() => {
    if (!prevPages || prevPages.length !== pages.length) {
      const current = typeof initialPage === 'number' ? initialPage : Math.max(0, pages.length - 1);

      setCurrentPage(current);
    }
  }, [pages]);

  // Prevents some later pages to be accessible before it is needed
  let prevValidity = true;

  return (
    <div ref={questionnaireRef} className={cx(className, styles.questionnaire)}>
      <Slider
        className={cx(styles.questionnaire__slider, loading && 'd-none')}
        activePage={currentPage}
        setActivePage={(newPage) => setCurrentPage(newPage)}
        showArrows={false}
        showDots={false}
        showNeighbourSlides={false}
        gesturesEnabled={false}
        customAnimationFunction={customAnimation}
      >
        {pages.map((props, i) => (
          <QuestionnairePage key={props.uuid} id={i} showTitle={showTitle} {...props}>
            {props.children}
            {isPageInvalid && i === currentPage && validationMessage && validationMessage(i, errors)}
          </QuestionnairePage>
        ))}
      </Slider>
      {loading && <InlineLoader />}

      <div className={styles.questionnaire__footer}>
        <div className={styles['questionnaire__footer-content']}>
          {showDots && (
            <div className={styles['questionnaire__footer-navigation']} data-testid="navigation-buttons">
              {pages.map((page, i) => {
                const validity = pages[i]
                  ? prevValidity &&
                    pages[i].fields.reduce(
                      (acc, currentField) =>
                        acc &&
                        !errors[currentField.name] &&
                        (Boolean(values[currentField.name]) || !currentField.required),
                      true
                    )
                  : true;

                prevValidity = validity;

                const isBtnDisabled = (!validity && currentPage !== i) || (currentPage < i && isPageInvalid);

                return (
                  <button
                    key={page.uuid}
                    className={cx(styles['questionnaire__footer-button'], {
                      [styles['questionnaire__footer-button--visited']]: validity,
                      [styles['questionnaire__footer-button--active']]: currentPage === i,
                      [styles['questionnaire__footer-button--disabled']]: isBtnDisabled,
                    })}
                    disabled={isBtnDisabled}
                    onClick={() => {
                      setCurrentPage(i);
                    }}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          )}
          {pages[currentPage] && pages[currentPage].customFooter ? (
            pages[currentPage].customFooter
          ) : (
            <div className={cx(styles['questionnaire__footer-block'], 'd-flex ml-md-auto order-0 order-md-1')}>
              {currentPage > 0 && (
                <Button outline className="mr-3" size="lg" onClick={prevPageHandler}>
                  {t('questionnaire.back')}
                </Button>
              )}
              <Button size="lg" disabled={isPageInvalid} onClick={nextPageHandler}>
                {t('questionnaire.next')}
              </Button>
            </div>
          )}
          {onReset && currentPage > 0 && (
            <Button outline type="reset" theme="secondary" className="mr-3" size="lg" onClick={resetHandler}>
              {t('questionnaire.start-over')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
